/**
 * The hand-off from a chat "Trend Analysis" answer to this page.
 *
 * WHAT TRAVELS, AND WHY IT IS AN ID
 * ---------------------------------
 * A chat trend answer and this page read the SAME three columns of the SAME
 * session table: `AgentMeasurement.category_text`, `.code` and `.value_unit`.
 * The backend therefore names each measurement it discussed with the very id
 * this page's inventory uses — `vital:8462-4@mm[Hg]` — built by the one
 * function that owns that format (`numeric_item_id`). Nothing here parses
 * prose, and nothing here reconstructs an id from a label.
 *
 * The label rides along ONLY so that a measurement which cannot be plotted can
 * be named to the clinician in the words they just read. It is never matched on.
 *
 * WHY ONE PARAMETER PER MEASUREMENT
 * ---------------------------------
 * `?plot=a&plot=b` rather than one comma-joined value: ids carry `:`, `@` and
 * `[]` (UCUM units such as `mm[Hg]`), and labels are free clinical text. Giving
 * each its own parameter lets URLSearchParams encode and decode every value
 * whole, so no separator inside the data can split a value in the wrong place.
 * The one separator we do use, `|`, splits on its FIRST occurrence: the id is
 * always the part before it, and an id has no room for a `|` (it is
 * type + LOINC code + UCUM unit), while a label might.
 */

import { MAX_NUMERIC_SERIES } from './selectionLimits';
import type { InventoryCategory } from './types';

/** Query parameter carrying one requested measurement. Repeated, not joined. */
export const HANDOFF_PARAM = 'plot';

export type HandoffTarget = {
  /** Inventory item id, e.g. `lab:2160-0@mg/dL`. */
  itemId: string;
  /** What the chat answer called it, for messages only. */
  label: string;
};

/**
 * `/patient/:id/trend-viz?plot=…` for the measurements a trend answer named.
 * Returns the path alone when there is nothing to plot, so a caller can always
 * link somewhere valid.
 */
export function buildHandoffUrl(patientId: string, targets: readonly HandoffTarget[]): string {
  const base = `/patient/${encodeURIComponent(patientId)}/trend-viz`;
  const usable = targets.filter((t) => t.itemId.trim());
  if (usable.length === 0) return base;
  const params = new URLSearchParams();
  for (const target of usable) {
    params.append(HANDOFF_PARAM, `${target.itemId}|${target.label ?? ''}`);
  }
  return `${base}?${params.toString()}`;
}

/** The measurements a hand-off URL asks for, in the order the answer listed them. */
export function readHandoffTargets(search: string): HandoffTarget[] {
  const params = new URLSearchParams(search ?? '');
  const targets: HandoffTarget[] = [];
  for (const raw of params.getAll(HANDOFF_PARAM)) {
    const separator = raw.indexOf('|');
    const itemId = (separator === -1 ? raw : raw.slice(0, separator)).trim();
    if (!itemId) continue;
    const label = separator === -1 ? '' : raw.slice(separator + 1).trim();
    targets.push({ itemId, label: label || itemId });
  }
  return targets;
}

/**
 * An id without its unit: `lab:2160-0@mg/dL` -> `lab:2160-0`.
 *
 * The fallback match runs on this. A chat answer reports ONE unit per
 * measurement (the dominant one) while the inventory keeps a separate leaf per
 * unit, so a chart that recorded the same analyte under two units can hand off
 * an id whose unit half does not exist as a leaf. The code half still does, and
 * that is the series the clinician meant.
 */
function codeKeyOf(itemId: string): string {
  const at = itemId.indexOf('@');
  return at === -1 ? itemId : itemId.slice(0, at);
}

export type HandoffResolution = {
  /** Items to select on arrival, already inside the per-class cap. */
  selectIds: string[];
  /** Asked for, present on the chart, but beyond the cap. */
  cappedOut: HandoffTarget[];
  /** Asked for and not on this chart at all (outside the window, or no numeric series). */
  unresolved: HandoffTarget[];
};

/**
 * Match what the answer asked for against what this chart actually holds.
 *
 * Nothing is dropped quietly: every requested measurement comes back in exactly
 * one of the three lists, so the page can always account for all of them.
 */
export function resolveHandoff(
  targets: readonly HandoffTarget[],
  categories: readonly InventoryCategory[],
): HandoffResolution {
  const byId = new Map<string, string>();
  const byCode = new Map<string, string>();
  for (const category of categories) {
    for (const item of category.items) {
      byId.set(item.id, item.id);
      // First leaf wins for a code with several units — the inventory is
      // already ranked most-recent-first, so that is the current one.
      const key = codeKeyOf(item.id);
      if (!byCode.has(key)) byCode.set(key, item.id);
    }
  }

  const matched: { target: HandoffTarget; id: string }[] = [];
  const unresolved: HandoffTarget[] = [];
  const seen = new Set<string>();
  for (const target of targets) {
    const id = byId.get(target.itemId) ?? byCode.get(codeKeyOf(target.itemId));
    if (!id) {
      unresolved.push(target);
      continue;
    }
    if (seen.has(id)) continue; // two requests collapsing onto one leaf
    seen.add(id);
    matched.push({ target, id });
  }

  return {
    selectIds: matched.slice(0, MAX_NUMERIC_SERIES).map((m) => m.id),
    cappedOut: matched.slice(MAX_NUMERIC_SERIES).map((m) => m.target),
    unresolved,
  };
}

const listOf = (targets: readonly HandoffTarget[]) => targets.map((t) => t.label).join(', ');

/**
 * One plain sentence per thing the clinician needs to know, or null when the
 * hand-off plotted everything it was asked for.
 *
 * Written so the clinician can act: the capped case says where to find the
 * rest, and the absent case says why it is absent rather than leaving them to
 * hunt for a measurement that is not there.
 */
export function handoffNotice(resolution: HandoffResolution): string | null {
  const parts: string[] = [];
  if (resolution.cappedOut.length > 0) {
    const total = resolution.selectIds.length + resolution.cappedOut.length;
    parts.push(
      `Plotted the first ${resolution.selectIds.length} of ${total} measurements from the answer. ` +
        `Not plotted: ${listOf(resolution.cappedOut)} — add them from the list on the left.`,
    );
  }
  if (resolution.unresolved.length > 0) {
    parts.push(
      `Not available on this chart: ${listOf(resolution.unresolved)} — ` +
        'outside the history window, or with no numeric series to plot.',
    );
  }
  return parts.length > 0 ? parts.join(' ') : null;
}
